/**
 * Manual transaction parser - converts text input to transaction data without AI
 */

interface ParsedTransaction {
  date: string;
  title: string;
  category: string;
  amount: number;
}

/**
 * Parses a single line of transaction text
 * Expected format: "Title amount" or "Title category amount"
 * Examples:
 *   - "Cơm trưa 50k" -> {title: "Cơm trưa", amount: 50000, category: "Ăn uống"}
 *   - "Xăng xe 200" -> {title: "Xăng xe", amount: 200000, category: "Di chuyển"}
 *   - "Mua sách 150000" -> {title: "Mua sách", amount: 150000, category: "Giáo dục"}
 */
export function parseTransactionLine(line: string, currentDate: string): ParsedTransaction {
  const trimmedLine = line.trim();
  
  if (!trimmedLine) {
    throw new Error('Empty line');
  }

  // Extract amount from the line (supports various formats)
  const amountMatch = trimmedLine.match(/(\d+(?:[.,]\d+)?)\s*([kKmMbB]|nghìn|triệu|tỷ|vnd|đồng|vnđ)?$/i);
  
  if (!amountMatch) {
    throw new Error(`Không tìm thấy số tiền trong: "${trimmedLine}"`);
  }

  const rawAmount = parseFloat(amountMatch[1].replace(',', '.'));
  const unit = amountMatch[2]?.toLowerCase() || '';
  
  // Convert to actual amount based on unit
  let amount = rawAmount;
  if (unit === 'k' || unit === 'nghìn') {
    amount = rawAmount * 1000;
  } else if (unit === 'm' || unit === 'triệu') {
    amount = rawAmount * 1000000;
  } else if (unit === 'b' || unit === 'tỷ') {
    amount = rawAmount * 1000000000;
  } else if (unit === 'vnd' || unit === 'đồng' || unit === 'vnđ') {
    // Keep as is
  } else if (rawAmount < 10000) {
    // If no unit and number is small, assume it's in thousands
    amount = rawAmount * 1000;
  }

  // Extract title (everything before the amount)
  const title = trimmedLine.substring(0, amountMatch.index).trim();
  
  if (!title) {
    throw new Error(`Không tìm thấy tiêu đề trong: "${trimmedLine}"`);
  }

  // Auto-detect category based on keywords
  const category = detectCategory(title);

  return {
    date: currentDate,
    title,
    category,
    amount: Math.round(amount)
  };
}

/**
 * Detects category based on transaction title keywords
 */
function detectCategory(title: string): string {
  const titleLower = title.toLowerCase();

  // Food & Drinks
  if (titleLower.match(/cơm|phở|bún|mì|ăn|uống|cà phê|cafe|trà|nước|quán|nhà hàng|buffet|lẩu|bánh|chè|kem/)) {
    return 'Ăn uống';
  }

  // Transportation
  if (titleLower.match(/xăng|xe|grab|taxi|bus|xe buýt|vé|tàu|máy bay|di chuyển|gửi xe|đỗ xe/)) {
    return 'Di chuyển';
  }

  // Shopping
  if (titleLower.match(/mua|shopping|quần áo|giày|dép|túi|phụ kiện|mỹ phẩm|trang điểm/)) {
    return 'Mua sắm';
  }

  // Education
  if (titleLower.match(/sách|học|khóa|course|lớp|giáo dục|học phí|văn phòng phẩm/)) {
    return 'Giáo dục';
  }

  // Entertainment
  if (titleLower.match(/phim|game|vui chơi|giải trí|du lịch|tour|khách sạn|resort/)) {
    return 'Giải trí';
  }

  // Health
  if (titleLower.match(/thuốc|bệnh viện|khám|y tế|sức khỏe|vitamin|gym|thể thao/)) {
    return 'Sức khỏe';
  }

  // Utilities
  if (titleLower.match(/điện|nước|gas|internet|wifi|điện thoại|tiện ích|hóa đơn/)) {
    return 'Tiện ích';
  }

  // Rent/Housing
  if (titleLower.match(/thuê|nhà|phòng|trọ|căn hộ/)) {
    return 'Nhà ở';
  }

  // Default category
  return 'Khác';
}

/**
 * Parses multiple lines of transaction text
 */
export function parseTransactions(text: string, type: 'income' | 'expense'): ParsedTransaction[] {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDate = new Date().getDate();
  const today = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${currentDate.toString().padStart(2, '0')}`;

  const lines = text.split('\n').filter(line => line.trim());
  const results: ParsedTransaction[] = [];
  const errors: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    try {
      const parsed = parseTransactionLine(lines[i], today);
      results.push(parsed);
    } catch (error) {
      errors.push(`Dòng ${i + 1}: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`);
    }
  }

  if (errors.length > 0 && results.length === 0) {
    throw new Error('Không thể phân tích giao dịch:\n' + errors.join('\n'));
  }

  return results;
}
